import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from '@/components/ui/LoadingState';
import { ArrowLeft, Eye } from 'lucide-react';
import MessageDetailsModal from './MessageDetailsModal';
import { calculateMessagesCostUsd, formatUsd } from './modelPricing';

interface Message {
  id: string;
  role: string;
  content: string | null;
  type?: string;
  created_at: string;
  human_agent_id?: string;
  message_metadata?: any;
  tool_args?: any;
}

interface Conversation {
  id: string;
  created_at: string;
  last_activity: string;
  status: string;
  organization_id?: string;
  organization?: {
    id: string;
    name: string;
  };
  lead?: {
    id: string;
    email: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  };
  agent?: {
    id: string;
    name: string;
  };
  messages?: Message[];
}

const ConversationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useApi();
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchConversation = async () => {
    try {
      setLoading(true);
      const response = await get(`/sessions/${id}?limit=5000&is_admin=true`);
      setConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchConversation();
    }
  }, [id]);

  const handleViewMessage = (message: Message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const getToolSummary = (message: Message): string => {
    const metadata = message.message_metadata || {};
    const toolCalls = Array.isArray(metadata.tool_calls) ? metadata.tool_calls : [];
    const functionCalls = Array.isArray(metadata.function_calls) ? metadata.function_calls : [];
    const toolNameFromMetadata = metadata.tool_name || metadata.name;

    const extractCallName = (call: any) =>
      call?.function?.name || call?.name || call?.tool_name || null;

    const callNames = [...toolCalls, ...functionCalls]
      .map(extractCallName)
      .filter((name): name is string => Boolean(name));

    if (callNames.length > 0) {
      return callNames.slice(0, 2).join(', ') + (callNames.length > 2 ? '…' : '');
    }
    if (toolNameFromMetadata) return String(toolNameFromMetadata);
    if (message.role === 'tool') return message.type || 'tool';
    return '—';
  };

  const columns: ColumnDef<Message>[] = [
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <Badge variant={row.original.role === 'user' ? 'default' : 'secondary'}>
          {row.original.role}
        </Badge>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => row.original.type || 'N/A',
    },
    {
      id: 'tool',
      header: 'Tool',
      cell: ({ row }) => (
        <div className="max-w-[220px] truncate" title={getToolSummary(row.original)}>
          {getToolSummary(row.original)}
        </div>
      ),
    },
    {
      accessorKey: 'content',
      header: 'Content',
      cell: ({ row }) => {
        const content = typeof row.original.content === 'string' ? row.original.content : '';
        const displayContent = content.length > 100 ? `${content.substring(0, 100)}...` : content;
        return (
          <div className="max-w-md truncate">
            {displayContent || '—'}
          </div>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => formatDate(row.original.created_at),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleViewMessage(row.original)}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const llmCostSummary = useMemo(
    () => calculateMessagesCostUsd(conversation?.messages),
    [conversation?.messages]
  );

  const table = useReactTable({
    data: conversation?.messages || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return <LoadingState label="Loading conversation..." className="min-h-[60vh]" />;
  }

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="text-muted-foreground mb-4">Conversation not found</div>
        <Button onClick={() => navigate('/admin')}>Back to Admin</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin')}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Button>
      </div>

      {/* Conversation Details */}
      <Card>
        <CardHeader>
          <CardTitle>Conversation Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">Conversation ID</div>
              <div className="text-sm">{conversation.id}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Organization</div>
              <div className="text-sm">{conversation.organization?.name || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Lead Email</div>
              <div className="text-sm">{conversation.lead?.email || 'Anonymous'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Lead Name</div>
              <div className="text-sm">
                {conversation.lead?.first_name || conversation.lead?.last_name
                  ? `${conversation.lead?.first_name || ''} ${conversation.lead?.last_name || ''}`.trim()
                  : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Agent</div>
              <div className="text-sm">{conversation.agent?.name || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Status</div>
              <Badge variant={conversation.status === 'active' ? 'default' : 'secondary'}>
                {conversation.status}
              </Badge>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Created At</div>
              <div className="text-sm">{formatDate(conversation.created_at)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Last Activity</div>
              <div className="text-sm">{formatDate(conversation.last_activity)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Estimated LLM Cost</div>
              <div className="text-sm font-semibold">
                {formatUsd(llmCostSummary.totalCostUsd, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {llmCostSummary.inputTokens.toLocaleString()} input + {llmCostSummary.outputTokens.toLocaleString()} output tokens
                {llmCostSummary.pricedMessageCount > 0
                  ? ` across ${llmCostSummary.pricedMessageCount} assistant turn${llmCostSummary.pricedMessageCount === 1 ? '' : 's'}`
                  : ''}
              </div>
              {llmCostSummary.unpricedModels.length > 0 && (
                <div className="text-xs text-amber-600 mt-1">
                  Missing pricing for: {llmCostSummary.unpricedModels.join(', ')}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages Table */}
      <Card>
        <CardHeader>
          <CardTitle>Messages ({conversation.messages?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No messages found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Message Details Modal */}
      {selectedMessage && (
        <MessageDetailsModal
          message={{ ...selectedMessage, content: selectedMessage.content ?? '' }}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedMessage(null);
          }}
        />
      )}
    </div>
  );
};

export default ConversationView;

